/**
 * Presentation for the builder AI assistant drawer.
 *
 * Request, draft, conflict, history, model, and Undo state live in
 * aiChatSession so exchanges can continue when this view is closed.
 */
/* global Marionette, define, jBox, jQuery, nfAiChat, _ */
define( 'views/app/drawer/contentAiAssistant',[
	'controllers/ai/aiChatSession',
	'nfAiModelPicker',
], function ( chatSession, ModelPicker ) {
	'use strict';

	const view = Marionette.ItemView.extend( {
		tagName: 'div',
		template: '#tmpl-nf-drawer-content-ai-assistant',

		events: {
			'click .nf-ai-chat-send': 'clickSend',
			'keydown textarea': 'maybeSend',
			'input textarea': 'autoGrow',
			'click .nf-ai-start-new-chat': 'startNewChat',
		},

		/**
		 * Scope drawer styling and land on the newest exchange.
		 *
		 * @return {void}
		 */
		onShow() {
			this.$el.closest( '#nf-drawer' ).addClass( 'nf-ai-drawer-active' );
			this.focusInput();
			_.defer( () => {
				if ( this.$log && this.$log.length ) {
					this.scrollLog();
				}
			} );
		},

		/**
		 * Cache presentation elements, attach the session, and render state.
		 *
		 * @return {void}
		 */
		onRender() {
			this.$log = this.$el.find( '.nf-ai-chat-log' );
			this.$input = this.$el.find( 'textarea' );
			this.$send = this.$el.find( '.nf-ai-chat-send' );
			this.$modelTrigger = this.$el.find( '.nf-ai-model-trigger' );
			this.$modelList = this.$el.find( '.nf-ai-model-list' );
			this.$startNewChat = this.$el.find( '.nf-ai-start-new-chat' );
			this.$modelControl = this.$el.find( '.nf-ai-model-control' );
			this.$providerField = jQuery( '<input type="hidden">' )
				.val( chatSession.selectedProvider() )
				.appendTo( this.$modelControl );
			this.$modelField = jQuery( '<input type="hidden">' )
				.val( chatSession.selectedModel() )
				.appendTo( this.$modelControl );
			this.modelPicker = new ModelPicker( {
				$element: this.$modelControl,
				$provider: this.$providerField,
				$model: this.$modelField,
				deferSelection: true,
				label: chatSession.i18n( 'providerChoose' ),
				onSelect: ( choice ) => chatSession.chooseModel( choice ),
			} );
			chatSession.attach( this );
			this.renderProviderSelector();
			this.renderTranscript();
			this.restoreComposerDraft();
			this.renderPreconditions();

			/*
			 * Not while the connect panel is showing: telling someone their
			 * model is unavailable, directly beneath an explanation that they
			 * have no provider at all, reads as two problems instead of one.
			 */
			if ( nfAiChat.providerDisconnected ) {
				if ( chatSession.providers().length ) {
					this.addMessage(
						'error',
						chatSession.i18n( 'providerDisconnected' )
					);
				}
				nfAiChat.providerDisconnected = false;
			}
			if ( chatSession.hasUndo() ) {
				this.addUndoChip();
			}
			if ( chatSession.isBusy() ) {
				this.setBusy( true );
			}
		},

		/**
		 * Detach presentation while preserving the shared session.
		 *
		 * @return {void}
		 */
		onBeforeDestroy() {
			this.$el
				.closest( '#nf-drawer' )
				.removeClass( 'nf-ai-drawer-active' );
			chatSession.rememberComposer(
				this.$input && this.$input.length ? this.$input.val() : ''
			);
			// Releases the picker's document-level dismiss listener, which
			// would otherwise outlive the drawer that owned it.
			if ( this.modelPicker ) {
				this.modelPicker.close();
			}
			chatSession.detach( this );
		},

		/**
		 * Say what is missing before a conversation can start.
		 *
		 * A missing provider was expressed by an empty model list and nothing
		 * else, which says what is absent but never what to do. The unsaved
		 * form is handled before the drawer opens at all.
		 *
		 * @return {void}
		 */
		renderPreconditions() {
			if ( ! chatSession.providers().length ) {
				this.renderConnectProvider();
			}
		},

		/**
		 * Offer the same explanation the dashboard tile gives, in the drawer.
		 *
		 * @return {void}
		 */
		renderConnectProvider() {
			/*
			 * A class, not the hidden attribute: .nf-ai-chat-controls and
			 * .nf-ai-chat-input both set display, which out-specifies the
			 * browser's [hidden] rule, so the composer stayed on screen under
			 * an explanation of why it could not be used.
			 */
			this.$el
				.find( '.nf-ai-chat-controls, .nf-ai-chat-input' )
				.addClass( 'nf-ai-hidden' );
			this.$log.empty();
			const $panel = jQuery( '<div>' ).addClass( 'nf-ai-connect' );
			jQuery( '<p>' )
				.addClass( 'nf-ai-connect-title' )
				.text( chatSession.i18n( 'connectTitle' ) )
				.appendTo( $panel );
			jQuery( '<p>' ).text( chatSession.i18n( 'connectBody' ) ).appendTo( $panel );
			jQuery( '<p>' ).text( chatSession.i18n( 'connectGoogle' ) ).appendTo( $panel );
			jQuery( '<a>' )
				.addClass( 'primary nf-button' )
				.attr( 'href', nfAiChat.connectUrl )
				.text( chatSession.i18n( 'connectAction' ) )
				.appendTo( $panel );
			$panel.appendTo( this.$log );
		},

		/**
		 * Put back text typed before the drawer was last closed.
		 *
		 * Closing the drawer destroys the composer, so anything unsent is gone
		 * unless the session held it. Leave the caret after the restored text
		 * rather than selecting it, so the next keystroke continues the message
		 * instead of replacing it.
		 *
		 * @return {void}
		 */
		restoreComposerDraft() {
			const retained = chatSession.composerDraft();
			if ( ! retained || ! this.$input || ! this.$input.length ) {
				return;
			}
			this.$input.val( retained );
			this.autoGrow();
			const end = retained.length;
			try {
				this.$input[ 0 ].setSelectionRange( end, end );
			} catch ( error ) {
				// A browser that refuses the caret still shows the text.
			}
		},

		/**
		 * Focus without pulling the off-canvas builder into view.
		 *
		 * @return {void}
		 */
		focusInput() {
			if ( ! this.$input || ! this.$input.length ) {
				return;
			}
			try {
				this.$input[ 0 ].focus( { preventScroll: true } );
			} catch ( error ) {
				// Older browsers skip focus rather than moving the canvas.
			}
		},

		/**
		 * Render the exact-model catalog through the shared picker.
		 *
		 * @return {void}
		 */
		renderProviderSelector() {
			this.$providerField.val( chatSession.selectedProvider() );
			this.$modelField.val( chatSession.selectedModel() );
			this.modelPicker.render(
				chatSession.providers(),
				{
					provider: chatSession.selectedProvider(),
					model: chatSession.selectedModel(),
				},
				chatSession.i18n( 'showAllModels' ),
				chatSession.i18n( 'providerChoose' )
			);
		},

		/**
		 * Render stored turns or the initial assistant greeting.
		 *
		 * @return {void}
		 */
		renderTranscript() {
			const stored = chatSession.history();
			if ( stored.length ) {
				_.each( stored, ( turn ) => {
					this.addMessage(
						'assistant' === turn.role ? 'assistant' : 'user',
						turn.content,
						turn
					);
				} );
				return;
			}
			this.addMessage(
				'assistant',
				chatSession.i18n( 'greeting' ),
				nfAiChat.assistant
			);
		},

		/**
		 * Confirm before clearing the accepted session context.
		 *
		 * @param {Event} event Click event.
		 * @return {void}
		 */
		startNewChat( event ) {
			event.preventDefault();
			event.stopPropagation();
			if ( chatSession.isBusy() ) {
				return;
			}
			new jBox( 'Confirm', {
				width: 440,
				content: chatSession.i18n( 'startNewChatConfirm' ),
				confirmButton: chatSession.i18n( 'startNewChatButton' ),
				cancelButton: chatSession.i18n( 'cancel' ),
				closeOnConfirm: true,
				confirm: chatSession.clear,
			} ).open();
		},

		/**
		 * Reset visible transcript after the session context clears.
		 *
		 * @return {void}
		 */
		resetConversation() {
			this.$log.empty();
			this.addMessage(
				'assistant',
				chatSession.i18n( 'greeting' ),
				nfAiChat.assistant
			);
			this.modelPicker.close();
			this.focusInput();
		},

		/**
		 * Scroll the transcript to its newest item.
		 *
		 * @return {void}
		 */
		scrollLog() {
			this.$log.scrollTop( this.$log[ 0 ].scrollHeight );
		},

		/**
		 * Format a Unix timestamp in the viewer's locale.
		 *
		 * @param {number} time Unix seconds.
		 * @return {string} Localized date/time.
		 */
		formatTime( time ) {
			if ( ! time ) {
				return '';
			}
			try {
				return new Date( time * 1000 ).toLocaleString( undefined, {
					year: 'numeric',
					month: 'short',
					day: 'numeric',
					hour: 'numeric',
					minute: '2-digit',
				} );
			} catch ( error ) {
				return '';
			}
		},

		/**
		 * Append one attributed message.
		 *
		 * @param {string} role Message role.
		 * @param {string} text Message text.
		 * @param {Object} meta Sender and timestamp metadata.
		 * @return {void}
		 */
		addMessage( role, text, meta ) {
			const $row = jQuery( '<div>' ).addClass( `nf-ai-row is-${ role }` );
			if ( meta && meta.name ) {
				const $sender = jQuery( '<div>' ).addClass( 'nf-ai-sender' );
				if ( meta.avatar ) {
					jQuery( '<img>' )
						.attr( { src: meta.avatar, alt: '' } )
						.appendTo( $sender );
				}
				jQuery( '<span>' ).text( meta.name ).appendTo( $sender );
				const when = this.formatTime( meta.time );
				if ( when ) {
					jQuery( '<span>' )
						.addClass( 'nf-ai-time' )
						.text( when )
						.appendTo( $sender );
				}
				$sender.appendTo( $row );
			}
			jQuery( '<div>' )
				.addClass( `nf-ai-msg is-${ role }` )
				.text( text )
				.appendTo( $row );
			$row.appendTo( this.$log );
			this.scrollLog();
		},

		/**
		 * Render request busy state.
		 *
		 * @param {boolean} state Busy state.
		 * @return {void}
		 */
		setBusy( state ) {
			this.$input.prop( 'disabled', state );
			this.$send.prop( 'disabled', state );
			this.$modelTrigger.prop( 'disabled', state );
			this.$startNewChat.prop( 'disabled', state );
			this.$modelList
				.find( '.nf-ai-model-option' )
				.prop( 'disabled', state );
			if ( state ) {
				const $busy = jQuery( '<div>' ).addClass(
					'nf-ai-msg is-assistant nf-ai-busy'
				);
				jQuery( '<span>' )
					.addClass( 'dashicons dashicons-update nf-ai-spin' )
					.appendTo( $busy );
				jQuery( '<span>' )
					.text( ` ${ chatSession.i18n( 'working' ) }` )
					.appendTo( $busy );
				$busy.appendTo( this.$log );
				this.scrollLog();
				return;
			}
			this.$log.find( '.nf-ai-busy' ).remove();
			this.recoverFocus();
		},

		/**
		 * Take focus back when busy state left it nowhere usable.
		 *
		 * Persisting a model choice disables the trigger the picker hands
		 * focus back to, so focus is left on the document or on the search
		 * field of a popup that has just been hidden. Once the drawer is
		 * usable again, put the caller back in the composer.
		 *
		 * @return {void}
		 */
		recoverFocus() {
			if ( ! this.$input || ! this.$input.length ) {
				return;
			}
			const owner = this.$input[ 0 ].ownerDocument;
			const active = owner ? owner.activeElement : null;
			if (
				active &&
				active !== owner.body &&
				'HTML' !== active.tagName &&
				0 < active.getClientRects().length
			) {
				return;
			}
			this.focusInput();
		},

		/**
		 * Send on Enter except while composing or when Shift is held.
		 *
		 * @param {KeyboardEvent} event Key event.
		 * @return {void}
		 */
		maybeSend( event ) {
			if ( event.isComposing || 229 === event.keyCode ) {
				return;
			}
			if ( 'Enter' === event.key && ! event.shiftKey ) {
				event.preventDefault();
				this.clickSend();
			}
		},

		/**
		 * Grow the composer to its bounded content height.
		 *
		 * @return {void}
		 */
		autoGrow() {
			this.$input.css( 'height', 'auto' );
			this.$input.css(
				'height',
				`${ Math.min( this.$input[ 0 ].scrollHeight, 120 ) }px`
			);
		},

		/**
		 * Validate and delegate a composer request.
		 *
		 * @return {void}
		 */
		clickSend() {
			if ( chatSession.isBusy() ) {
				return;
			}
			const text = this.$input.val().trim();
			if ( ! text ) {
				this.focusInput();
				return;
			}
			if (
				! chatSession.selectedProvider() ||
				! chatSession.selectedModel()
			) {
				this.addMessage(
					'error',
					chatSession.i18n( 'providerRequired' )
				);
				this.$modelTrigger.trigger( 'focus' );
				return;
			}
			this.$input.val( '' ).css( 'height', 'auto' );
			chatSession.rememberComposer( '' );
			chatSession.send( text );
		},

		/**
		 * Delegate the latest AI Undo.
		 *
		 * @return {void}
		 */
		clickUndo() {
			chatSession.undo();
		},

		/**
		 * Restore a failed request to the composer.
		 *
		 * @param {Object} payload Original request payload.
		 * @return {void}
		 */
		restoreInput( payload ) {
			if ( ! payload.message ) {
				return;
			}
			this.$input.val( payload.message );
			this.autoGrow();
		},

		/**
		 * Offer the latest AI Undo beneath its reply.
		 *
		 * @return {void}
		 */
		addUndoChip() {
			this.removeUndoChip();
			const $chip = jQuery(
				'<button type="button" class="nf-ai-undo"></button>'
			)
				.text( chatSession.i18n( 'undo' ) )
				.on( 'click', () => this.clickUndo() );
			jQuery( '<div>' )
				.addClass( 'nf-ai-undo-row' )
				.append( $chip )
				.appendTo( this.$log );
			this.scrollLog();
		},

		/**
		 * Remove any visible Undo chip.
		 *
		 * @return {void}
		 */
		removeUndoChip() {
			if ( this.$log ) {
				this.$log.find( '.nf-ai-undo-row' ).remove();
			}
		},
	} );

	return view;
} );

